// RhymeSchemePlugin - Read-only rhyme scheme overlay rendering
// Renders letter badges (A, B, C, etc.) in left gutter to show rhyme patterns
// Uses categorical rhyme detection with stability-aware visual encoding
// Respects user preference: preferences.feedback.rhyme.enabled and .showLabels

import { useEffect, useRef, useState, useCallback } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEditorPreferences } from '../../../hooks/useEditorPreferences'
import { $getRoot, $isParagraphNode } from 'lexical'
import { $isSectionBlockNode } from '../nodes/SectionBlockNode'
import { detectRhymeCategory } from '../../../utils/rhyme/categoryDetector'
import type { RhymeCategory } from '../../../utils/rhyme/types'
import { CATEGORY_WEIGHTS } from '../../../utils/rhyme/types'
import { getPhoneticSignature } from '../../../utils/rhyme/phonetics'
import {
  calculateSchemeAwareOpacity,
  getExpectedRhymePositionWithCustom
} from '../../../utils/rhyme/stabilityInterpreter'
import type { SectionRhymeSchemes } from '../../../types/rhymeScheme'
import { DEFAULT_SECTION_SCHEMES } from '../../../types/rhymeScheme'
import RhymeBadge, { type ConnectedRhyme } from '../components/RhymeBadge'
import RhymePopover from '../components/RhymePopover'

// ============================================================================
// Types for Rhyme Rendering
// ============================================================================

/**
 * Descriptor for a single rhyme badge to render
 *
 * Stores position, visual encoding, metadata, and connected rhyme data.
 */
interface RhymeBadgeDescriptor {
  /** Unique key for React rendering (nodeKey:lineIndex) */
  key: string
  /** Unique ID for event handling */
  id: string
  /** X position relative to editor root */
  x: number
  /** Y position relative to editor root */
  y: number
  /** Rhyme scheme letter (A, B, C, etc.) */
  letter: string
  /** Rhyme category for accessibility and visual encoding */
  category: RhymeCategory
  /** Callback subscript for cross-section callbacks (A₁, A₂, etc.) */
  callbackSubscript?: number
  /** Border style based on category strength */
  borderStyle: 'solid' | 'dashed' | 'dotted'
  /** Opacity based on stability expectation match */
  opacity: number
  /** Whether this rhyme matches stability expectations */
  isExpected: boolean
  /** Whether this is a related rhyme (A' vs A) - Scope 22 */
  isRelated: boolean
  /** Connected rhymes in this group (for popover) */
  connectedRhymes: ConnectedRhyme[]
  /** The rhyming word at this line */
  word: string
  /** Line number in the document (1-indexed) */
  lineNumber: number
}

// ============================================================================
// Weight-Tier Letter Assignment (Scope 22)
// ============================================================================

/**
 * Thresholds for weight-tier letter assignment
 *
 * Strong rhymes (weight >= 0.85) → reuse existing letter (A stays A)
 * Mid-tier rhymes (0.70 <= weight < 0.85) → reuse letter but mark as related (A becomes A')
 * Weak rhymes (weight < 0.70) → start new letter (advance to B)
 */
const SAME_LETTER_THRESHOLD = 0.85  // PERFECT + FAMILY_VOICED
const RELATED_LETTER_THRESHOLD = 0.70  // FAMILY_UNVOICED + ADDITIVE + SUBTRACTIVE

/**
 * Determine letter assignment based on rhyme weight
 *
 * Pure function for unit testing - no side effects
 *
 * @param weight - Rhyme category weight (from CATEGORY_WEIGHTS)
 * @param existingLetter - The letter of the matched rhyme group (null if no match)
 * @param currentLetter - The next available letter for new groups
 * @returns Letter assignment decision with metadata
 */
export function determineLetterAssignment(
  weight: number,
  existingLetter: string | null,
  currentLetter: string
): { letter: string; isRelated: boolean; advanceLetter: boolean } {
  // No existing group - create new one, but check weight for the joining word
  // (first word of the pair is stored separately with isRelated: false as the reference)
  if (!existingLetter) {
    // Mid-tier rhymes (0.70-0.85) get prime notation on the second word
    const isRelatedByWeight = weight >= RELATED_LETTER_THRESHOLD && weight < SAME_LETTER_THRESHOLD
    return {
      letter: currentLetter,
      isRelated: isRelatedByWeight,
      advanceLetter: true
    }
  }

  // Strong rhyme - join existing group as same letter
  if (weight >= SAME_LETTER_THRESHOLD) {
    return {
      letter: existingLetter,
      isRelated: false,
      advanceLetter: false
    }
  }

  // Mid-tier rhyme - join existing group as related letter (A')
  if (weight >= RELATED_LETTER_THRESHOLD) {
    return {
      letter: existingLetter,
      isRelated: true,
      advanceLetter: false
    }
  }

  // Weak rhyme - start new group
  return {
    letter: currentLetter,
    isRelated: false,
    advanceLetter: true
  }
}

// ============================================================================
// Plugin Component
// ============================================================================

/**
 * RhymeSchemePlugin - Renders read-only rhyme scheme labels with distance-based opacity
 *
 * Architecture:
 * - ONE overlay container mounted inside Lexical root
 * - Detects rhyme patterns using categorical detection (Scope 17)
 * - Applies distance-based opacity calculation (Scope 18 followup)
 * - Positions labels in left gutter (24px to left of editor root)
 * - Throttled updates (~500ms) to avoid excessive recalculation
 * - Resets rhyme sequence per SectionBlockNode boundary
 * - EXCEPTION: prechorus→chorus does NOT reset (callback continuation)
 *
 * Visual encoding:
 * - Letter assignment for all rhymes (no threshold filtering)
 * - Border style indicates category strength (solid/dashed/dotted)
 * - Font weight indicates weight tier (400-700)
 * - Opacity based on distance from expected rhyme distance (1-10 stability scale)
 * - Callback subscripts track cross-section rhyme reuse (A₁, A₂, etc.)
 *
 * Performance optimizations:
 * - Throttled recalculation with requestAnimationFrame guard
 * - Batched DOM reads for position measurements
 * - Section-aware processing with letter tracking
 *
 * @param stabilityLevel - Numeric stability level (1-10), defaults to 5 (medium)
 * @param sectionSchemes - Per-section-type rhyme scheme configuration (Scope 21)
 */
export default function RhymeSchemePlugin({
  stabilityLevel = 5,
  sectionSchemes: sectionSchemesProp = DEFAULT_SECTION_SCHEMES
}: {
  stabilityLevel?: number
  sectionSchemes?: SectionRhymeSchemes | null
} = {}) {
  const [editor] = useLexicalComposerContext()
  // Handle null explicitly - default params only catch undefined
  const sectionSchemes = sectionSchemesProp ?? DEFAULT_SECTION_SCHEMES
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [badges, setBadges] = useState<RhymeBadgeDescriptor[]>([])
  const rafScheduledRef = useRef(false)
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { preferences } = useEditorPreferences()

  // Interactive state for badges
  const [activeBadgeId, setActiveBadgeId] = useState<string | null>(null)
  const [highlightedGroup, setHighlightedGroup] = useState<string | null>(null)
  // Track the active badge's wrapper element for popover positioning
  const [activeBadgeElement, setActiveBadgeElement] = useState<HTMLElement | null>(null)

  // Respect user preferences - hide when disabled or labels turned off
  const isEnabled = preferences.feedback.rhyme.enabled && preferences.feedback.rhyme.showLabels

  // Badge event handlers
  const handleBadgeMouseEnter = useCallback((id: string) => {
    // Find the letter group for this badge
    const badge = badges.find(b => b.id === id)
    if (badge) {
      setHighlightedGroup(badge.letter)
    }
  }, [badges])

  const handleBadgeMouseLeave = useCallback(() => {
    setHighlightedGroup(null)
  }, [])

  const handleBadgeClick = useCallback((id: string) => {
    // Toggle popover: close if same badge, open if different
    setActiveBadgeId(prev => {
      if (prev === id) {
        // Closing - clear the reference element
        setActiveBadgeElement(null)
        return null
      }
      return id
    })
  }, [])

  // Close popover on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeBadgeId) {
        setActiveBadgeId(null)
        setActiveBadgeElement(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [activeBadgeId])

  // Close popover on outside click
  useEffect(() => {
    if (!activeBadgeId) return

    const handleOutsideClick = (e: MouseEvent) => {
      // Check if click is outside the popover and badges
      const target = e.target as HTMLElement
      if (!target.closest('.rhyme-badge') && !target.closest('.rhyme-popover')) {
        setActiveBadgeId(null)
        setActiveBadgeElement(null)
      }
    }

    // Use capture phase to intercept before editor handles the click
    document.addEventListener('click', handleOutsideClick, true)
    return () => document.removeEventListener('click', handleOutsideClick, true)
  }, [activeBadgeId])

  // Cleanup throttle timeout on unmount
  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
      }
    }
  }, [])

  // Recalculate rhyme labels based on current editor state
  const recalculateLabels = useCallback(() => {
    if (rafScheduledRef.current) return

    rafScheduledRef.current = true
    requestAnimationFrame(() => {
      rafScheduledRef.current = false

      const rootElement = editor.getRootElement()
      if (!rootElement || !containerRef.current) {
        return
      }

      // PHASE 1: Collect line data per section from editor state
      interface SectionData {
        sectionKey: string
        sectionType: string  // verse, chorus, bridge, etc.
        paragraphs: Array<{
          nodeKey: string
          text: string
        }>
      }

      const sections: SectionData[] = []
      let currentSection: SectionData | null = null

      editor.getEditorState().read(() => {
        const root = $getRoot()
        const children = root.getChildren()

        // Track if we're in a standalone paragraph group
        let standaloneGroup: SectionData | null = null

        for (const child of children) {
          // Section boundary - start new section
          if ($isSectionBlockNode(child)) {
            // Push any pending standalone group first
            if (standaloneGroup) {
              sections.push(standaloneGroup)
              standaloneGroup = null
            }

            if (currentSection) {
              sections.push(currentSection)
            }
            currentSection = {
              sectionKey: child.getKey(),
              sectionType: child.getSectionType(),
              paragraphs: []
            }

            // Collect paragraphs within this section
            const sectionChildren = child.getChildren()
            for (const sectionChild of sectionChildren) {
              if ($isParagraphNode(sectionChild)) {
                const text = sectionChild.getTextContent().trim()
                if (text) {
                  currentSection.paragraphs.push({
                    nodeKey: sectionChild.getKey(),
                    text
                  })
                }
              }
            }

            // Push completed section
            sections.push(currentSection)
            currentSection = null
          } else if ($isParagraphNode(child)) {
            // Paragraph outside sections - group consecutive ones together
            const text = child.getTextContent().trim()
            if (text) {
              // Create standalone group if not exists
              if (!standaloneGroup) {
                standaloneGroup = {
                  sectionKey: `standalone-group`,
                  sectionType: 'standalone',
                  paragraphs: []
                }
              }

              standaloneGroup.paragraphs.push({
                nodeKey: child.getKey(),
                text
              })
            }
          }
        }

        // Push final standalone group
        if (standaloneGroup) {
          sections.push(standaloneGroup)
        }
      })

      // PHASE 2: Process sections asynchronously (detectRhymeCategory is async)
      // Use categorical detection with distance-based opacity calculation
      const processRhymes = async () => {
        const rootRect = rootElement.getBoundingClientRect()

        // Track letter groups by PHONETIC SIGNATURE for proper grouping
        // Using phonetic signatures ensures words with same rhyming sound share a group:
        // - "head" and "said" both map to "EH_D" → same group
        // - "rain" and "pain" both map to "EY_N" → same group
        // Map: phoneticSignature → { letter, originSectionIndex, firstLineIndex, nodes, callbackCount }
        const rhymeGroups = new Map<string, {
          letter: string;
          originSectionIndex: number;
          firstLineIndex: number; // Global line index of first occurrence
          nodes: Array<{ nodeKey: string; sectionIndex: number; lineIndex: number }>;
          callbackCount: number;
        }>()

        // Track which node keys have been assigned letters, categories, distances, and related status
        // Map: nodeKey → { letter, callbackSubscript, category, distance, isRelated, groupKey, lineIndexInSection, sectionType, globalLineIndex, sectionIndex }
        const nodeKeyToLetter = new Map<string, {
          letter: string;
          callbackSubscript?: number;
          category: RhymeCategory;
          distance: number;
          isRelated: boolean;  // Scope 22: track mid-tier rhymes (A' vs A)
          groupKey: string;    // Phonetic signature for proper connected rhymes grouping
          lineIndexInSection: number;  // Scope 21: line position within section for scheme calculation
          sectionType: string;  // Scope 21: section type for scheme lookup
          globalLineIndex: number;  // Global line index (0-based across entire document)
          sectionIndex: number;  // Which instance of this section type (0 = first verse, 1 = second verse)
        }>()

        let currentLetter = 'A'
        let globalLineIndex = 0 // Track global line number across all sections

        for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
          const section = sections[sectionIndex]
          const lines = section.paragraphs.map(p => p.text)

          if (lines.length === 0) continue

          // Get previous section type for prechorus→chorus detection
          const previousSectionType = sectionIndex > 0 ? sections[sectionIndex - 1].sectionType : undefined

          // Check if this is prechorus→chorus transition (callback continuation)
          const isCallbackContinuation =
            previousSectionType === 'prechorus' && section.sectionType === 'chorus'

          // Reset letter assignment unless it's a callback continuation
          if (!isCallbackContinuation && sectionIndex > 0) {
            currentLetter = 'A'
            // Preserve rhymeGroups across sections for callback tracking
            // Note: nodeKeyToLetter persists across sections for callback detection
          }

          // Process each line pair-wise to detect rhymes
          for (let i = 0; i < lines.length; i++) {
            const currentGlobalLineIndex = globalLineIndex + i // Track absolute line position
            const currentLine = lines[i]
            const paragraphData = section.paragraphs[i]

            if (!paragraphData || !currentLine) continue

            // Extract last word for rhyme detection
            const words = currentLine.split(/\s+/)
            const lastWord = words[words.length - 1]?.replace(/[.,!?;:"']/g, '') || ''

            if (!lastWord) continue

            // Compare with all previous lines in this section to find rhyme match
            // NO THRESHOLD FILTERING - all rhymes get labels with distance-based opacity
            let bestMatch: { category: RhymeCategory; matchIndex: number; matchGlobalLineIndex: number } | null = null
            let bestWeight = 0

            // DEBUG: Log what we're processing
            if (process.env.NODE_ENV === 'development') {
              console.log(`[RhymeSchemePlugin] Processing line ${i}: "${lastWord}"`)
            }

            for (let j = 0; j < i; j++) {
              const prevGlobalLineIndex = globalLineIndex + j
              const prevLine = lines[j]
              const prevWords = prevLine.split(/\s+/)
              const prevLastWord = prevWords[prevWords.length - 1]?.replace(/[.,!?;:"']/g, '') || ''

              if (!prevLastWord) continue

              // Detect rhyme category between words
              // Args: (reference, current) so ADDITIVE means current adds phonemes to reference
              // e.g., "line" vs "lined" → ADDITIVE (lined = line + d)
              const match = await detectRhymeCategory(prevLastWord, lastWord)

              // DEBUG: Log detection results
              if (process.env.NODE_ENV === 'development' && match.category !== 'NONE') {
                console.log(`  [RhymeSchemePlugin] "${lastWord}" vs "${prevLastWord}": ${match.category} (weight: ${match.weight})`)
              }

              // NO THRESHOLD - accept ANY detected rhyme (even weak ones like ASSONANCE/CONSONANCE)
              // Opacity will vary based on distance, not weight
              if (match.weight > bestWeight) {
                bestMatch = { category: match.category, matchIndex: j, matchGlobalLineIndex: prevGlobalLineIndex }
                bestWeight = match.weight
              }
            }

            // DEBUG: Log best match result
            if (process.env.NODE_ENV === 'development') {
              if (bestMatch) {
                console.log(`  [RhymeSchemePlugin] Best match for "${lastWord}": line ${bestMatch.matchIndex} (${bestMatch.category})`)
              } else {
                console.log(`  [RhymeSchemePlugin] No within-section match for "${lastWord}"`)
              }
            }

            // ALSO check for cross-section rhymes if no match found within section
            // This enables callback detection across section boundaries
            // IMPORTANT: Use higher threshold for cross-section to prevent false callbacks
            if (!bestMatch) {
              // Get phonetic signature for current word
              const currentSignature = await getPhoneticSignature(lastWord)

              // Check if current word would be a cross-section callback
              const currentCallbackGroup = currentSignature
                ? rhymeGroups.get(currentSignature)
                : undefined
              const wouldBeCallback = currentCallbackGroup &&
                currentCallbackGroup.originSectionIndex < sectionIndex

              // CRITICAL FIX: Before checking cross-section, look ahead to see if any LATER
              // line in this section would rhyme with the current word.
              // EXCEPTION: If BOTH current AND later word would be callbacks to the SAME group,
              // let the callback happen (don't skip). This handles "strain/gain" both being
              // callbacks to an earlier "rain/pain" group.
              let shouldSkipCrossSection = false

              for (let k = i + 1; k < lines.length; k++) {
                const laterLine = lines[k]
                const laterWords = laterLine.split(/\s+/)
                const laterLastWord = laterWords[laterWords.length - 1]?.replace(/[.,!?;:"']/g, '') || ''

                if (!laterLastWord) continue

                // Args: (reference, current) - same order as main detection
                const laterMatch = await detectRhymeCategory(laterLastWord, lastWord)
                // Use a reasonable threshold - if the later word would form a real rhyme
                if (laterMatch.weight >= 0.50) {
                  // Check if later word would ALSO be a callback to the same group
                  const laterSignature = await getPhoneticSignature(laterLastWord)
                  const laterWouldBeCallback = laterSignature &&
                    laterSignature === currentSignature &&
                    wouldBeCallback

                  if (laterWouldBeCallback) {
                    // Both words would be callbacks to same group - DON'T skip cross-section
                    // Let current word establish the callback, later word will join
                    if (process.env.NODE_ENV === 'development') {
                      console.log(`  [RhymeSchemePlugin] "${lastWord}" and "${laterLastWord}" both callback to same group`)
                      console.log(`  [RhymeSchemePlugin] Processing cross-section callback for "${lastWord}"`)
                    }
                  } else {
                    // Later word would form a NEW within-section pair - skip cross-section
                    shouldSkipCrossSection = true
                    if (process.env.NODE_ENV === 'development') {
                      console.log(`  [RhymeSchemePlugin] Found later match: "${lastWord}" will rhyme with "${laterLastWord}" (line ${k}, ${laterMatch.category})`)
                      console.log(`  [RhymeSchemePlugin] Skipping cross-section check - waiting for within-section pair`)
                    }
                  }
                  break
                }
              }

              // Check cross-section if we shouldn't skip
              if (!shouldSkipCrossSection) {
                // DEBUG: Log phonetic signature lookup
                if (process.env.NODE_ENV === 'development') {
                  console.log(`  [RhymeSchemePlugin] Checking cross-section for "${lastWord}" (signature: ${currentSignature || 'null'})`)
                  console.log(`  [RhymeSchemePlugin] Existing group keys: ${Array.from(rhymeGroups.keys()).join(', ') || '(none)'}`)
                }

                if (currentSignature && currentCallbackGroup && wouldBeCallback) {
                  // Found exact phonetic match from earlier section - this is a true callback
                  // Use PERFECT weight since it's an exact phonetic signature match
                  bestMatch = {
                    category: 'PERFECT' as RhymeCategory, // Signature match = perfect rhyme
                    matchIndex: -1, // -1 indicates cross-section
                    matchGlobalLineIndex: currentCallbackGroup.firstLineIndex
                  }
                  bestWeight = CATEGORY_WEIGHTS['PERFECT' as RhymeCategory]

                  // Store the phonetic signature for later lookup
                  ;(bestMatch as { category: RhymeCategory; matchIndex: number; matchGlobalLineIndex: number; phoneticSignature?: string }).phoneticSignature = currentSignature
                }
              }
            }

            // Assign letter based on best match
            let assignedLetter = ''
            let callbackSubscript: number | undefined
            let distance = 0 // Distance from first rhyme occurrence
            let isRelated = false // Scope 22: track mid-tier rhymes (A' vs A)

            if (bestMatch) {
              // Calculate distance from match
              distance = currentGlobalLineIndex - bestMatch.matchGlobalLineIndex

              // DEBUG: Log distance calculation
              if (process.env.NODE_ENV === 'development') {
                console.log(`  [RhymeSchemePlugin] Distance for "${lastWord}": current=${currentGlobalLineIndex}, match=${bestMatch.matchGlobalLineIndex}, distance=${distance}`)
              }

              // Handle cross-section callback (matchIndex = -1)
              const isCrossSectionMatch = bestMatch.matchIndex === -1

              // CRITICAL FIX: For within-section matches, join the MATCHED word's group
              // This ensures "lined" joins "line"'s group even though they have different
              // phonetic signatures (ADDITIVE rhyme: AY_N vs AY_N_D)
              let existingGroup: {
                letter: string;
                originSectionIndex: number;
                firstLineIndex: number;
                nodes: Array<{ nodeKey: string; sectionIndex: number; lineIndex: number }>;
                callbackCount: number;
              } | undefined
              let existingGroupKey: string | undefined // Track the phonetic signature key for connected rhymes
              let newGroupKey: string | undefined // The key when creating a new group

              if (isCrossSectionMatch) {
                // Cross-section rhyme - use the stored phonetic signature
                const crossSectionSignature = (bestMatch as { category: RhymeCategory; matchIndex: number; matchGlobalLineIndex: number; phoneticSignature?: string }).phoneticSignature || null
                if (crossSectionSignature) {
                  existingGroup = rhymeGroups.get(crossSectionSignature)
                  existingGroupKey = crossSectionSignature // Store the key for connected rhymes
                }
              } else {
                // Same-section rhyme - find the MATCHED word's existing group
                // First, check if the matched word already has a letter assigned
                const matchedParagraph = section.paragraphs[bestMatch.matchIndex]
                const matchedWords = lines[bestMatch.matchIndex].split(/\s+/)
                const matchedLastWord = matchedWords[matchedWords.length - 1]?.replace(/[.,!?;:"']/g, '') || ''
                const matchedLetterInfo = nodeKeyToLetter.get(matchedParagraph.nodeKey)

                // DEBUG: Log the matched word lookup
                if (process.env.NODE_ENV === 'development') {
                  console.log(`  [RhymeSchemePlugin] Looking up matched word "${matchedLastWord}" (line ${bestMatch.matchIndex})`)
                  console.log(`  [RhymeSchemePlugin] matchedLetterInfo: ${matchedLetterInfo ? `letter=${matchedLetterInfo.letter}` : 'null (not yet assigned)'}`)
                }

                if (matchedLetterInfo) {
                  // Matched word already has a group - find it
                  // Search ALL groups, not just current section (word might have joined cross-section callback)
                  for (const [groupPhoneticKey, group] of rhymeGroups.entries()) {
                    if (group.letter === matchedLetterInfo.letter) {
                      // For cross-section callbacks, the matched word might be in a group from another section
                      // Check if the matched word's nodeKey is actually in this group
                      const matchedInGroup = group.nodes.some(n => n.nodeKey === matchedParagraph.nodeKey)
                      if (matchedInGroup) {
                        existingGroup = group
                        existingGroupKey = groupPhoneticKey // Capture key for connected rhymes
                        break
                      }
                    }
                  }
                }
              }

              if (existingGroup) {
                // Existing rhyme group - determine if same letter or related (A vs A')
                // Use weight-tier assignment logic (Scope 22)
                const tierAssignment = determineLetterAssignment(
                  bestWeight,
                  existingGroup.letter,
                  currentLetter
                )

                assignedLetter = tierAssignment.letter
                isRelated = tierAssignment.isRelated

                // DEBUG: Log joining existing group with tier info
                if (process.env.NODE_ENV === 'development') {
                  const relatedMarker = isRelated ? "'" : ''
                  console.log(`  [RhymeSchemePlugin] JOINING existing group "${assignedLetter}${relatedMarker}" for "${lastWord}" (weight: ${bestWeight})`)
                }

                // Check if this is a callback (rhyme from earlier section)
                if (existingGroup.originSectionIndex < sectionIndex) {
                  // Callback! Only increment callbackCount if this is the FIRST callback from this section
                  const hasCallbacksFromThisSection = existingGroup.nodes.some(
                    node => node.sectionIndex === sectionIndex
                  )

                  if (!hasCallbacksFromThisSection) {
                    // First callback from this section - increment counter
                    existingGroup.callbackCount++
                  }

                  // Each callback section gets its own subscript:
                  // - Section 2 (first callback) gets subscript 1
                  // - Section 3 (second callback) gets subscript 2
                  // - etc.
                  // DO NOT update previous callback sections - they keep their original subscript
                  callbackSubscript = existingGroup.callbackCount
                }

                // Add this node to the group with its section and line index
                existingGroup.nodes.push({ nodeKey: paragraphData.nodeKey, sectionIndex, lineIndex: currentGlobalLineIndex })
              } else if (!isCrossSectionMatch) {
                // New rhyme group within section - use weight-tier assignment
                // Determine if we should create new group or join as related (Scope 22)
                const tierAssignment = determineLetterAssignment(
                  bestWeight,
                  null, // No existing group
                  currentLetter
                )

                assignedLetter = tierAssignment.letter
                isRelated = tierAssignment.isRelated

                // Get the matched word for signature
                const matchedParagraph = section.paragraphs[bestMatch.matchIndex]
                const matchedGlobalLineIndex = bestMatch.matchGlobalLineIndex
                const matchedWords = lines[bestMatch.matchIndex].split(/\s+/)
                const matchedLastWord = matchedWords[matchedWords.length - 1]?.replace(/[.,!?;:"']/g, '') || ''
                const matchedSignature = await getPhoneticSignature(matchedLastWord)

                // Use matched word's signature as the group key
                const groupKey = matchedSignature || `group-${assignedLetter}`
                newGroupKey = groupKey // Store for nodeKeyToLetter entries

                // DEBUG: Log new group creation with tier info
                if (process.env.NODE_ENV === 'development') {
                  const relatedMarker = isRelated ? "'" : ''
                  console.log(`  [RhymeSchemePlugin] Creating NEW group "${assignedLetter}${relatedMarker}" (key: ${groupKey}) for "${matchedLastWord}" + "${lastWord}" (weight: ${bestWeight})`)
                }

                rhymeGroups.set(groupKey, {
                  letter: assignedLetter,
                  originSectionIndex: sectionIndex,
                  firstLineIndex: matchedGlobalLineIndex,
                  nodes: [
                    { nodeKey: matchedParagraph.nodeKey, sectionIndex, lineIndex: matchedGlobalLineIndex },
                    { nodeKey: paragraphData.nodeKey, sectionIndex, lineIndex: currentGlobalLineIndex }
                  ],
                  callbackCount: 0
                })

                // Advance letter only if tierAssignment says to (strong/mid-tier don't advance)
                if (tierAssignment.advanceLetter) {
                  currentLetter = String.fromCharCode(currentLetter.charCodeAt(0) + 1)
                }

                // Store the FIRST line of the rhyme pair (matched paragraph) with distance=0
                if (!nodeKeyToLetter.has(matchedParagraph.nodeKey)) {
                  nodeKeyToLetter.set(matchedParagraph.nodeKey, {
                    letter: assignedLetter,
                    category: bestMatch.category,
                    distance: 0, // First occurrence has distance 0
                    isRelated: false, // First occurrence is never related
                    groupKey, // Phonetic signature for connected rhymes grouping
                    lineIndexInSection: bestMatch.matchIndex, // Scope 21: line position within section
                    sectionType: section.sectionType, // Scope 21: section type for scheme lookup
                    globalLineIndex: matchedGlobalLineIndex, // Global line index for section scoping
                    sectionIndex, // Which instance of this section type
                  })
                }
              }

              // Store this line's letter mapping (only if we have an assigned letter)
              if (assignedLetter) {
                // Use existing group key if joining, or new group key if creating
                // Fall back to letter if somehow neither is set (shouldn't happen in normal flow)
                const resolvedGroupKey = existingGroupKey || newGroupKey || `group-${assignedLetter}`
                nodeKeyToLetter.set(paragraphData.nodeKey, {
                  letter: assignedLetter,
                  callbackSubscript,
                  category: bestMatch.category,
                  distance,
                  isRelated, // Scope 22: track mid-tier rhymes (A' vs A)
                  groupKey: resolvedGroupKey, // Phonetic signature for connected rhymes grouping
                  lineIndexInSection: i, // Scope 21: line position within section (0-indexed)
                  sectionType: section.sectionType, // Scope 21: section type for scheme lookup
                  globalLineIndex: currentGlobalLineIndex, // Global line index for section scoping
                  sectionIndex, // Which instance of this section type
                })
              }
            }
          }

          // Increment global line index for next section
          globalLineIndex += lines.length
        }

        // PHASE 3: Build connected rhymes map for each rhyme group
        // CRITICAL: Use phonetic signature (groupKey), NOT letter, as the key
        // This ensures only words that actually rhyme together are grouped,
        // even if they share the same letter (e.g., "B" in Verse 1 vs "B" in Chorus)
        // Map: groupKey (phonetic signature) → ConnectedRhyme[]
        const connectedRhymesMap = new Map<string, ConnectedRhyme[]>()

        // First pass: collect all words and line numbers per rhyme group
        for (const [nodeKey, letterInfo] of nodeKeyToLetter.entries()) {
          // Use groupKey (phonetic signature) for proper rhyme grouping
          // Fall back to letter only if groupKey is somehow missing
          const mapKey = letterInfo.groupKey || letterInfo.letter

          // Find the line number for this nodeKey
          let lineNumber = 1
          let word = ''

          // Search through sections to find this node
          for (const section of sections) {
            for (let i = 0; i < section.paragraphs.length; i++) {
              if (section.paragraphs[i].nodeKey === nodeKey) {
                // Extract last word from this line
                const text = section.paragraphs[i].text
                const words = text.split(/\s+/)
                word = words[words.length - 1]?.replace(/[.,!?;:"']/g, '') || ''
                break
              }
              lineNumber++
            }
            if (word) break
          }

          // Add to connected rhymes for this group
          if (!connectedRhymesMap.has(mapKey)) {
            connectedRhymesMap.set(mapKey, [])
          }

          connectedRhymesMap.get(mapKey)!.push({
            lineNumber,
            word,
            isCurrent: false, // Will be updated per badge
          })
        }

        // PHASE 4: Generate badges from nodeKeyToLetter map with connected rhymes
        const newBadges: RhymeBadgeDescriptor[] = []
        let badgeLineNumber = 1

        for (const [nodeKey, letterInfo] of nodeKeyToLetter.entries()) {
          const paragraphDom = editor.getElementByKey(nodeKey)
          if (!paragraphDom) continue

          const paragraphRect = paragraphDom.getBoundingClientRect()

          // Calculate scheme-aware opacity (Scope 21)
          // Get the configured rhyme scheme for this section type
          const sectionScheme = sectionSchemes[letterInfo.sectionType] || { pattern: 'FREE' }
          const expectedDistance = getExpectedRhymePositionWithCustom(
            sectionScheme.pattern,
            sectionScheme.customPattern,
            letterInfo.lineIndexInSection
          )

          // Use scheme-aware opacity that penalizes deviation from expected pattern
          const opacity = calculateSchemeAwareOpacity(
            letterInfo.distance,
            expectedDistance,
            stabilityLevel
          )

          // Determine border style based on category weight
          const weight = CATEGORY_WEIGHTS[letterInfo.category]
          let borderStyle: 'solid' | 'dashed' | 'dotted'
          if (weight >= 0.85) {
            borderStyle = 'solid'
          } else if (weight >= 0.70) {
            borderStyle = 'dashed'
          } else {
            borderStyle = 'dotted'
          }

          // isExpected based on opacity threshold (high opacity = expected)
          const isExpected = opacity >= 0.8

          // Position in left gutter (relative to editor root, not viewport)
          const xPosition = paragraphRect.left - rootRect.left - 24
          const yPosition = paragraphRect.top - rootRect.top + (paragraphRect.height / 2) - 10 // Center 20px badge

          // Get connected rhymes for this rhyme group (using phonetic signature, not letter)
          const rhymeGroupKey = letterInfo.groupKey || letterInfo.letter
          const connectedRhymes = (connectedRhymesMap.get(rhymeGroupKey) || []).map(rhyme => ({
            ...rhyme,
            isCurrent: rhyme.lineNumber === badgeLineNumber,
          }))

          // Extract word for this line
          let word = ''
          for (const section of sections) {
            for (const paragraph of section.paragraphs) {
              if (paragraph.nodeKey === nodeKey) {
                const words = paragraph.text.split(/\s+/)
                word = words[words.length - 1]?.replace(/[.,!?;:"']/g, '') || ''
                break
              }
            }
            if (word) break
          }

          // DEBUG: Log badge creation with key values
          if (process.env.NODE_ENV === 'development') {
            console.log(`[RhymeSchemePlugin] Creating badge: word="${word}", letter=${letterInfo.letter}, distance=${letterInfo.distance}, expectedDist=${expectedDistance}, sectionType=${letterInfo.sectionType}`)
          }

          newBadges.push({
            key: `rhyme-${nodeKey}`,
            id: `badge-${nodeKey}`,
            x: xPosition,
            y: yPosition,
            letter: letterInfo.letter,
            category: letterInfo.category,
            callbackSubscript: letterInfo.callbackSubscript,
            borderStyle,
            opacity,
            isExpected,
            isRelated: letterInfo.isRelated,
            connectedRhymes,
            word,
            lineNumber: badgeLineNumber,
          })

          badgeLineNumber++
        }

        // PHASE 5: Update state
        setBadges(newBadges)

        // Log in development
        if (process.env.NODE_ENV === 'development') {
          console.log(`[RhymeSchemePlugin] Calculated ${newBadges.length} rhyme badges across ${sections.length} sections`)
          // Debug: Show letter assignments
          console.log('[RhymeSchemePlugin] Letter assignments:')
          for (const badge of newBadges) {
            console.log(`  ${badge.letter}: "${badge.key}" (category: ${badge.category}, ${badge.connectedRhymes.length} connected)`)
          }
        }
      }

      // Kick off async processing
      processRhymes().catch(error => {
        console.error('[RhymeSchemePlugin] Error processing rhymes:', error)
      })
    })
  }, [editor, stabilityLevel, sectionSchemes])

  // Throttled recalculation - trigger on text changes but don't spam
  const throttledRecalculate = useCallback(() => {
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current)
    }

    throttleTimeoutRef.current = setTimeout(() => {
      recalculateLabels()
    }, 500) // 500ms throttle like stress marks
  }, [recalculateLabels])

  // Register update listener for editor changes
  useEffect(() => {
    if (!isEnabled) return

    return editor.registerUpdateListener(() => {
      throttledRecalculate()
    })
  }, [editor, isEnabled, throttledRecalculate])

  // Initial calculation on mount
  useEffect(() => {
    if (isEnabled) {
      recalculateLabels()
    }
  }, [isEnabled, recalculateLabels])

  // Early return after hooks
  if (!isEnabled) {
    return null
  }

  // Render interactive badges with popovers
  // z-index: 10 when popover active ensures it appears above stress overlay (z-index: 5)
  return (
    <div
      ref={containerRef}
      className="rhyme-scheme-layer"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'auto', // Enable pointer events for interactivity
        zIndex: activeBadgeId ? 50 : 5, // Elevate significantly when popover open to beat stress overlay (z-index 5)
      }}
    >
      {badges.map((badge) => {
        const isActive = badge.id === activeBadgeId
        const isHighlighted = highlightedGroup === badge.letter && !isActive
        const isDimmed = activeBadgeId !== null && badge.id !== activeBadgeId

        return (
          <div
            key={badge.key}
            ref={(el) => {
              // Capture the wrapper element when this badge becomes active
              // @floating-ui/dom uses this to position the popover smartly
              if (isActive && el) {
                setActiveBadgeElement(el)
              }
            }}
            style={{
              // Position wrapper at badge location so popover renders correctly
              position: 'absolute',
              left: `${badge.x}px`,
              top: `${badge.y}px`,
              // Elevate active badge wrapper to ensure popover is above stress marks
              zIndex: isActive ? 1000 : undefined,
            }}
          >
            <RhymeBadge
              id={badge.id}
              letter={badge.letter}
              x={0} // Badge at origin of positioned wrapper
              y={0}
              category={badge.category}
              callbackSubscript={badge.callbackSubscript}
              isRelated={badge.isRelated}
              borderStyle={badge.borderStyle}
              opacity={badge.opacity}
              isActive={isActive}
              isHighlighted={isHighlighted}
              isDimmed={isDimmed}
              connectedRhymes={badge.connectedRhymes}
              onMouseEnter={handleBadgeMouseEnter}
              onMouseLeave={handleBadgeMouseLeave}
              onClick={handleBadgeClick}
            />

            {/* Render popover for active badge - uses @floating-ui/dom for smart positioning */}
            {isActive && (
              <RhymePopover
                letter={badge.letter}
                category={badge.category}
                isRelated={badge.isRelated}
                referenceElement={activeBadgeElement}
                connectedRhymes={badge.connectedRhymes}
                isExpected={badge.isExpected}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
